import type {
  ApiDashboardElement,
  Collection,
} from '@highimpact-frontend/api-types';

import type { DashboardHostContextValue } from '../context/DashboardHostContext.types';
import {
  getName,
  makeEmptyImage,
  makeEmptyImageGallery,
  makeEmptyTable,
  makeEmptyText,
  setElementEntityScopeFromWidgetEntity,
  setName,
} from '../state/dashboard-element-operations';
import {
  buildWidgetEntityFromHost,
} from './formHandlerShared';

/**
 * Rebuilds a builtin-group initiator element when the user switches widget type,
 * preserving the current name
 */
export function rebuildInitiatorElementForType(
  elementType: Exclude<
    ApiDashboardElement['element_type'],
    'collection' | 'readonlytext'
  >,
  previous: Exclude<ApiDashboardElement, Collection>,
  options: {
    readonly host: DashboardHostContextValue;
    readonly startDate: Date;
    readonly endDate: Date;
    readonly preserveUnconfigured: boolean;
  }
): Exclude<ApiDashboardElement, Collection> {
  const name = getName(previous) ?? '';

  const seedEntityScope = <T extends Exclude<ApiDashboardElement, Collection>>(
    element: T
  ): T => {
    const entity = buildWidgetEntityFromHost(options.host);
    return entity
      ? (setElementEntityScopeFromWidgetEntity(entity, element) as T)
      : element;
  };

  let next: Exclude<ApiDashboardElement, Collection>;

  switch (elementType) {
    case 'table':
      next = makeEmptyTable(
        {
          start_date: options.startDate.toISOString(),
          end_date: options.endDate.toISOString(),
        },
        options.preserveUnconfigured
      );
      break;
    case 'text':
      next = makeEmptyText(false);
      break;
    case 'image':
      next = seedEntityScope(makeEmptyImage());
      break;
    case 'imagegallery':
      next = seedEntityScope(makeEmptyImageGallery());
      break;
    default:
      return previous;
  }

  return setName(name, next) as Exclude<ApiDashboardElement, Collection>;
}
